<template>
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <expansion-card
      icon="mdi-account"
      class="pb-1"
    >
      <span slot="title">Server</span>
      <v-container
        class="pa-0"
        fluid
      >
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <v-text-field
              v-model="itemData.firstName"
              label="First Name *"
              :error-messages="getPathErrors(jsonPath, 'itemData.firstName')"
              :disabled="isFormDisabled"
              required
              :rules="rules().required('First Name')"
              clearable
              @input="clearPathErrors(jsonPath, 'itemData.firstName')"
            />
          </v-col>
          <v-spacer />
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <v-text-field
              v-model="itemData.nickname"
              label="Nickname"
              :error-messages="getPathErrors(jsonPath, 'itemData.nickname')"
              :disabled="isFormDisabled"
              clearable
              @input="clearPathErrors(jsonPath, 'itemData.nickname')"
            />
          </v-col>
          <v-col
            cols="12"
            sm="5"
            md="2"
            align-self="center"
            class="px-3 pt-3 d-flex justify-end"
          >
            <v-boolean
              v-model="itemData.active"
              label="Active"
              :error-messages="getPathErrors(jsonPath, 'itemData.active')"
              :field-errors="getPathErrors(jsonPath, 'itemData.active')"
              :disabled="isFormDisabled"
            />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <v-text-field
              v-model="itemData.lastName"
              label="Last Name *"
              :error-messages="getPathErrors(jsonPath, 'itemData.lastName')"
              :disabled="isFormDisabled"
              required
              :rules="rules().required('Last Name')"
              clearable
              @input="clearPathErrors(jsonPath, 'itemData.lastName')"
            />
          </v-col>
          <v-spacer />
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <v-text-field
              v-model="itemData.phone"
              label="Phone #"
              :error-messages="getPathErrors(jsonPath, 'itemData.phone')"
              :disabled="isFormDisabled"
              clearable
              @input="clearPathErrors(jsonPath, 'itemData.phone')"
            />
          </v-col>
          <v-spacer />
        </v-row>
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <div class="d-flex justify-space-between">
              <number-field
                v-model="itemData.maxTables"
                label="Max Tables *"
                :rules="[...rules().numeric(1, undefined), ...rules().decimals(0), ...rules().required('Max Tables')]"
                :disabled="isFormDisabled"
              />
              <number-field
                v-model="itemData.maxCovers"
                label="Max Covers *"
                :rules="[...rules().numeric(1, undefined), ...rules().decimals(0), ...rules().required('Max Covers')]"
                :disabled="isFormDisabled"
              />
            </div>
          </v-col>
          <v-spacer />
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <v-text-field
              v-model="itemData.email"
              label="Email"
              :error-messages="getPathErrors(jsonPath, 'itemData.email')"
              :disabled="isFormDisabled"
              clearable
              @input="clearPathErrors(jsonPath, 'itemData.email')"
            />
          </v-col>
          <v-spacer />
        </v-row>
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3"
          >
            <location-meal-period-linked-input 
              :server-id="itemData.id"
              :locations-options="locations"
              :locations="itemData.locations"
              :meal-periods="itemData.mealPeriods"
              :disabled="isFormDisabled"
              @input:locations="$set(itemData, 'locations', $event)"
              @input:meal-periods="$set(itemData, 'mealPeriods', $event)"
            />
          </v-col>
          <v-spacer />
          <v-col
            cols="12"
            sm="5"
            md="4"
            class="px-3 pt-3 d-flex justify-space-around"
          >
            <div class="d-flex flex-column">
              <div>
                Color *:
                <server-initial-icon 
                  :server="itemData"
                  size="65"
                />
              </div>
              <text-color-selector 
                v-model="itemData.textColor"
                label="Text Color"
                class="pt-8"
              />
            </div>
            <v-color-picker 
              v-model="itemData.color"
              mode="hexa"
              hide-canvas
              show-swatches
              hide-mode-switch
              hide-inputs
              swatches-max-height="200px"
            />
          </v-col>
          <v-spacer />
        </v-row>
      </v-container>
    </expansion-card>
  </item-editor-dialog>
</template>

<script>
import {itemEdit, withChildren} from '@/ft-vue-core'
import serverMixins from './mixins/serverMixins'
import ServerInitialIcon from '../../floorPlan/helperComponents/ServerInitialIcon.vue'
import TextColorSelector from '../tables/floorPlan/selectedElement/TextColorSelector.vue'
export default {
  name: 'ServerItem',
  components: {ServerInitialIcon, TextColorSelector},
  mixins: [itemEdit, withChildren, serverMixins],
  data: function () {
    return {
      type: 'Server',
      types: 'Servers',
      apiRecordCommand: 'diningServer',
      itemDefault: {
        active: true,
        color: '#FFFFFF'
      },
      children: true,
    }
  },
  watch: {
    $route () {
      if (this.showItem) 
        this.$set(this.itemData, 'locations', this.itemData.locations?.map(l => l.locationId) ?? [])
    },
  },
  methods: {
    itemResourceCommands () {
      return [{command: 'diningLocation', load: this.locations, loading: this.isLoading}]
    },
  }
}
</script>